import React, { MutableRefObject, useCallback, useEffect, useRef } from "react";
import { CardScanError, generateError } from "../api/cardApiError";
import styles from "./scanVideoViewer.module.scss";
import { useGlobalContext } from "./ScanViewerContext";
import { State } from "xstate";
import { ScanViewContext, ScanViewEvent } from "./scanViewStateMachine";
import { GlobalLogger } from "./debugLogger";
import CameraPermissionModal from "./permissionModal";

declare global {
  interface MediaDeviceInfo {
    getCapabilities(): MediaTrackCapabilities;
  }
}

type Props = {
  videoRef: React.RefObject<HTMLVideoElement>;
  setShowErrorModal: React.Dispatch<React.SetStateAction<boolean>>;
  stateMachineSend: any;
  // Why did this break?
  // send: (event: string, payload?: any) => void;
  setScanDebugState: React.Dispatch<React.SetStateAction<string>>;
  onError: ((error: CardScanError) => void) | undefined;
  captureCanvasRef: React.RefObject<HTMLCanvasElement>;
  setVideoMetadata: React.Dispatch<
    React.SetStateAction<{ [key: string]: any }>
  >;
  isMobile: boolean;
  isVideoPortrait: boolean;
};

const ScanVideoViewer = ({
  videoRef,
  setShowErrorModal,
  stateMachineSend,
  setScanDebugState,
  onError,
  setVideoMetadata,
  isMobile,
  isVideoPortrait,
}: Props) => {
  const initialized = useRef(false);
  const {
    stateMachineService,
    stateMachineState,
    showErrorModal,
    cameraPermissionModalConfig,
    webToMobileHandoffConfig,
  } = useGlobalContext();

  const srcObject = useRef() as MutableRefObject<MediaStream | null>;

  const getVideo = useCallback(
    async (videoBg: HTMLVideoElement) => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        GlobalLogger.error(
          "Video Error - mediaDevices or getUserMedia undefined"
        );
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            width: { min: 1280, ideal: 1920 },
            height: { min: 720, ideal: 1080 },
            facingMode: "environment",
          },
        });

        setShowErrorModal(false);
        srcObject.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        const videoTrack = stream.getVideoTracks().pop();
        const settings = videoTrack?.getSettings() || {};
        const videoMetadata: { [key: string]: any } = {
          videoTrack: settings,
        };

        //only works on chrome powered
        navigator.mediaDevices.enumerateDevices().then((devices) => {
          const device = devices
            .filter((device) => device["kind"] === "videoinput")
            .pop();

          const currentDevice = devices.find(
            (device) => device.deviceId === settings.deviceId
          );

          if (currentDevice) {
            videoMetadata.cameraDeviceInfo = currentDevice;
          }

          // getCapabilities won't likely be supported by TS
          // https://github.com/microsoft/TypeScript/issues/45660
          if (device && device.getCapabilities != undefined) {
            const caps = device.getCapabilities();
            videoMetadata.cameraCapabilities = caps;
          }
        });

        setVideoMetadata(videoMetadata);

        // //1024x768 or less if video is smaller. If canvas is over 1024 we have GPU memory issues.
        // const captureCanvas = captureCanvasRef.current;
        // if (captureCanvas && settings.width && settings.height) {
        //   captureCanvas.width = Math.min(settings.width, 1024);
        //   captureCanvas.height = Math.min(settings.height, 768);
        // }

        new Promise<MediaStream>((resolve) => {
          videoBg.onloadedmetadata = () => {
            resolve(stream);
          };
        });
      } catch (error: any) {
        const err = generateError(error.message, "VideoError", 677);
        GlobalLogger.error(`Video Error: ${error.message} - 677`);
        initialized.current = false;

        if (onError) {
          onError(err);
        }
        //Only show error modal when error is a permissions error.
        if (error.name === "NotAllowedError") {
          setShowErrorModal(true);
          stateMachineSend("CAMERAERROR");
          GlobalLogger.debug("Camera Error: Not Allowed");
        } else if (error.name === "NotFoundError") {
          setShowErrorModal(true);
          stateMachineSend("CAMERAERROR");
          GlobalLogger.debug("Camera Error: Not Found");
        } else {
          setShowErrorModal(false);
          stateMachineSend("ERROR");
          GlobalLogger.debug("Camera Error: Unknown");
        }
      }
    },
    [
      onError,
      stateMachineSend,
      setScanDebugState,
      setShowErrorModal,
      setVideoMetadata,
      stateMachineState.context.modelState,
    ]
  );

  const loadVideo = () => {
    const videoBg = videoRef.current;
    if (videoBg) {
      videoBg.srcObject = srcObject.current;
      const videoPromise = videoBg.play();

      if (videoPromise === undefined) {
        GlobalLogger.error("Video Error - videoPromise undefined");
        initialized.current = false;
        return Promise.reject(new Error("Video promise is undefined"));
      }

      videoPromise
        .then(() => {
          stateMachineSend("CAMERA_LOADED");
          setScanDebugState("loading");
          GlobalLogger.debug("Camera Loaded");
          GlobalLogger.log("Video Started");
        })
        .catch((error: any) => {
          const err = generateError(error.message, "VideoError", 675);
          GlobalLogger.error(`Video Error: ${error.message} - 675`);
          initialized.current = false;

          if (onError) {
            onError(err);
          }
          stateMachineSend("ERROR");
        });
    }
  };

  const retryCamera = useCallback(async () => {
    try {
      stateMachineSend("retryCamera");

      if (srcObject.current) {
        srcObject.current.getTracks().forEach((track) => track.stop());
      }
      if (videoRef.current) {
        await getVideo(videoRef.current);
        if (cameraPermissionModalConfig?.onRetry) {
          cameraPermissionModalConfig.onRetry();
        }
      }
    } catch (error: any) {
      const err = generateError(error.message, "VideoError", 675);
      GlobalLogger.error(`Video Error: ${error.message} - 675`);
      initialized.current = false;
      if (onError) onError(err);
      stateMachineSend("ERROR");
    }
  }, [getVideo, onError, stateMachineSend, videoRef]);

  useEffect(() => {
    const videoBg = videoRef.current;

    if (videoBg && !initialized.current) {
      initialized.current = true;
      getVideo(videoBg);
    }
  }, [videoRef, getVideo, onError, initialized]);

  useEffect(() => {
    const subscription = stateMachineService.subscribe(
      (state: State<ScanViewContext, ScanViewEvent>) => {
        if (state.matches("eligibilityProcessing")) {
          videoRef.current?.pause();
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [stateMachineService]);

  useEffect(() => {
    if (
      stateMachineState.context.modelState === "warmed" &&
      videoRef.current &&
      srcObject.current
    ) {
      loadVideo();

      return () => {
        srcObject.current?.getTracks().forEach((track) => {
          track.stop();
        });
      };
    }
  }, [stateMachineState.context.modelState, videoRef, srcObject.current]);

  return (
    <>
      <CameraPermissionModal
        cameraPermissionModalConfig={cameraPermissionModalConfig}
        retry={retryCamera}
        enableModel={
          showErrorModal &&
          (cameraPermissionModalConfig?.enabled ?? true) &&
          (!webToMobileHandoffConfig?.enabled || isMobile)
        }
      />
      <video
        data-cy="cardscanVideo"
        ref={videoRef}
        className={`${styles.videoBackground} ${
          isMobile ? styles.mobile : styles.fullScreen
        } ${isVideoPortrait ? styles.portraitView : ""}`}
        id="frame"
        muted
        playsInline
        autoPlay
      ></video>
    </>
  );
};

export default ScanVideoViewer;
